import { DateTimeUtils } from "@stellar/utils";
/** Number of characters in an ISO date-only string (YYYY-MM-DD). */
const ISO_DATE_LENGTH = 10;

/** Minimum proportion of tickStep gap needed to also render the final label. */
const X_TICK_LAST_LABEL_MIN_GAP_RATIO = 0.6;

/** A single summary card from the analytics dashboard response. */

/**
 * Builds the KPI delta from a summary card. The KPI bar derives the sign and
 * color from `trend`, so we pass the raw magnitude and let the component decide.
 */
function buildCardDelta(card) {
  return {
    percentage: card.deltaPercent ?? 0,
    trend: card.trend ?? "flat",
    description: "from previous period"
  };
}

/** Maps analytics summary cards to KPI bar items keyed by their metric key. */
export function buildKpiBarItems(summary) {
  if (!summary) {
    return [];
  }
  return Object.entries(summary).flatMap(([metricKey, card]) => {
    if (!card) {
      return [];
    }
    return [{
      id: metricKey,
      value: card.value,
      label: card.label,
      delta: buildCardDelta(card)
    }];
  });
}

/**
 * Formats a Date as an ISO date-only string in UTC (YYYY-MM-DD).
 *
 * Note: output uses UTC because it relies on Date.toISOString().
 */
export function formatDateOnly(date) {
  return date.toISOString().slice(0, ISO_DATE_LENGTH);
}

/**
 * Builds a date-only query range from the selected `[start, end]` tuple,
 * matching the dashboard API query contract.
 * If either date is missing, it defaults to today or the other date, and ensures
 * that the `from` date is always less than or equal to the `to` date.
 * The returned query values are formatted as ISO date-only strings (YYYY-MM-DD).
 *
 * @param selectedRange The selected `[start, end]` date range tuple.
 * @returns An object with `from` and `to` properties for the dashboard query.
 */
export function buildDateRangeQuery(selectedRange) {
  const [startDate, endDate] = selectedRange;
  const today = new Date();
  const fromCandidate = startDate ?? today;
  const toCandidate = endDate ?? fromCandidate;
  const fromDate = fromCandidate <= toCandidate ? fromCandidate : toCandidate;
  const toDate = fromCandidate <= toCandidate ? toCandidate : fromCandidate;
  return {
    from: formatDateOnly(fromDate),
    to: formatDateOnly(toDate)
  };
}

/** Returns the selected metric value for a project item, defaulting to 0 when missing. */
function getMetricValue(value) {
  return value ?? 0;
}

/** Formats a Date as a short month+day label (e.g. "Jun 1"). */
function formatDayLabel(date) {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}

/** Creates a zero-initialized dataset with one entry per day in the selected range. */
function createEmptyDailyMetricDataset(totalDays, startDate) {
  return Array.from({
    length: totalDays
  }, (_, index) => ({
    label: formatDayLabel(new Date(startDate.getTime() + index * DateTimeUtils.MILLISECONDS_IN_DAY)),
    value: 0
  }));
}

/**
 * Maps project metrics from Analytics API to chart-friendly daily points for a selected metric.
 */
export function buildDailyMetricDataset({
  projectMetrics,
  startDate,
  totalDays,
  metricKey
}) {
  const byDay = createEmptyDailyMetricDataset(totalDays, startDate);
  if (!projectMetrics) {
    return byDay;
  }
  const fromTimestamp = DateTimeUtils.getDayEdge({
    date: projectMetrics.from,
    edge: "start",
    shouldUseUtc: true
  }).getTime();
  let pointsInRange = 0;
  projectMetrics.items.forEach(item => {
    const createdTimestamp = DateTimeUtils.getDayEdge({
      date: item.createdDate,
      edge: "start",
      shouldUseUtc: true
    }).getTime();
    const dayIndex = Math.floor((createdTimestamp - fromTimestamp) / DateTimeUtils.MILLISECONDS_IN_DAY);
    if (dayIndex < 0 || dayIndex >= totalDays) {
      return;
    }
    const metricValue = getMetricValue(item.metrics[metricKey]);
    byDay[dayIndex].value += metricValue;
    pointsInRange += metricValue;
  });
  if (totalDays > 0 && pointsInRange === 0 && projectMetrics.items.length > 0) {
    projectMetrics.items.forEach((item, index) => {
      const dayIndex = Math.min(Math.floor(index * totalDays / projectMetrics.items.length), totalDays - 1);
      byDay[dayIndex].value += getMetricValue(item.metrics[metricKey]);
    });
  }
  return byDay;
}

/**
 * Decides whether a date label should be rendered on the X-axis for a given tick index.
 *
 * Uses the computed `tickStep` to skip intermediate labels and, for the final day,
 * renders it only when there is enough gap from the previous rendered step label.
 */
export function shouldRenderXTickLabel(index, totalItems, tickStep) {
  if (totalItems <= 1) {
    return true;
  }
  const lastIndex = totalItems - 1;
  const isStepTick = index % tickStep === 0;
  if (index !== lastIndex) {
    return isStepTick;
  }
  if (isStepTick) {
    return true;
  }
  const previousStepTick = Math.floor(lastIndex / tickStep) * tickStep;
  const minGapToRenderLastTick = Math.ceil(tickStep * X_TICK_LAST_LABEL_MIN_GAP_RATIO);
  return lastIndex - previousStepTick >= minGapToRenderLastTick;
}